import { clearanceService } from '../services/clearanceService'

/**
 * Upload a document to GCS using presigned URL
 * @param {number} requestId - Clearance request ID
 * @param {File} file - File to upload
 * @param {string} documentType - Document type (e.g., 'commercial_invoice')
 * @param {Function} onProgress - Progress callback (percent)
 * @returns {Promise<Object>} Document object with id and status
 */
export const uploadDocumentToGCS = async (requestId, file, documentType, onProgress) => {
  try {
    // Step 1: Get presigned URL
    const presignResult = await clearanceService.presignDocument(requestId, {
      documentType,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
    })

    if (!presignResult.success) {
      throw new Error(presignResult.error || 'Failed to get upload URL')
    }

    const { documentId, uploadUrl, publicUrl, filePath } = presignResult.data

    // Step 2: Upload file to GCS with progress
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', uploadUrl, true)
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')

      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            onProgress(percent)
          }
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText || xhr.status}`))
        }
      }

      xhr.onerror = () => reject(new Error('Upload failed – network error'))
      xhr.send(file)
    })

    // Step 3: Confirm document upload to backend
    const confirmResult = await clearanceService.confirmDocumentUpload(requestId, documentId)
    if (!confirmResult.success) {
      console.warn('Failed to confirm document upload:', confirmResult.error)
    }

    // Step 4: Return document info
    return { id: documentId, status: 'UPLOADED' }
  } catch (error) {
    console.error('Document upload error:', error)
    throw error
  }
}

/**
 * Delete a document (if needed, e.g., when replacing an existing one)
 * @param {number} documentId - Document ID to delete
 */
export const deleteDocument = async (documentId) => {
  // If your backend has a DELETE /documents/:id endpoint, call it here
  // For now, you can omit or implement later.
  console.warn('deleteDocument not implemented – add if needed')
}